/* Il registro delle ore: cercare, raggruppare, esportare.
 *
 * Chiesto da Edoardo il 25 settembre 2026, notte: chi imputa deve poter
 * controllare di aver messo tutto, chi fattura deve vedere il lavoro di un
 * cliente o di una persona, e tutti e due devono poterlo portare in Excel.
 *
 * ## Le azioni, prima della grafica
 *
 * 1. **Restringere**: un periodo (quasi sempre questo mese o il mese scorso,
 *    quindi a un clic), poi cliente, persona, prestazione, stato, parole.
 * 2. **Leggere per gruppi**: le stesse righe per cliente, per persona o per
 *    giorno, con il totale di ogni gruppo. Per giorno e' il controllo di chi
 *    imputa: un giorno che manca si vede.
 * 3. **Portarlo via**: il CSV e' esattamente quello che si vede.
 *
 * **I filtri stanno nell'indirizzo**, cosi' "le ore di Bianchi quest'anno" e'
 * un collegamento: la scheda del cliente porta qui gia' filtrata, e un
 * indirizzo si puo' mandare a un collega.
 *
 * Chi non vede le ore di tutti riceve dal server solo le sue: qui il filtro
 * per persona semplicemente non compare. Il permesso non sta in questa pagina.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ErroreApi, anagrafica, ore } from "../api";
import { useCarica } from "../carica";
import { CampoCerca } from "../componenti/CampoCerca";
import {
  conta,
  nomeTipo,
  oggi,
  oreEMinuti,
  type Attivita,
  type Cliente,
  type FiltroRegistro,
  type Pratica,
  type Registro,
  type RigaRegistro,
  type StatoFatturazione,
} from "../dominio";
import { useSessione } from "../sessione";

type Gruppo = "cliente" | "persona" | "giorno" | "nessuno";

const GRUPPI: { valore: Gruppo; nome: string }[] = [
  { valore: "cliente", nome: "Cliente" },
  { valore: "persona", nome: "Persona" },
  { valore: "giorno", nome: "Giorno" },
  { valore: "nessuno", nome: "Nessuno" },
];

const STATI: { valore: StatoFatturazione; nome: string }[] = [
  { valore: "tutte", nome: "Tutte" },
  { valore: "da_fatturare", nome: "Da fatturare" },
  { valore: "fatturate", nome: "Fatturate" },
];

// ------------------------------------------------------------------ date

function iso(d: Date): string {
  const mese = String(d.getMonth() + 1).padStart(2, "0");
  const giorno = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mese}-${giorno}`;
}

function periodi(): { nome: string; dal: string; al: string }[] {
  const oggiData = new Date();
  const anno = oggiData.getFullYear();
  const mese = oggiData.getMonth();
  const lunedi = new Date(oggiData);
  lunedi.setDate(oggiData.getDate() - ((oggiData.getDay() + 6) % 7));
  return [
    { nome: "Questa settimana", dal: iso(lunedi), al: oggi() },
    { nome: "Questo mese", dal: iso(new Date(anno, mese, 1)), al: oggi() },
    {
      nome: "Mese scorso",
      dal: iso(new Date(anno, mese - 1, 1)),
      al: iso(new Date(anno, mese, 0)),
    },
    { nome: "Quest'anno", dal: `${anno}-01-01`, al: oggi() },
  ];
}

/** "lun 21 set": corta, perche' sta in una colonna. */
function dataCorta(giorno: string): string {
  const [a, m, g] = giorno.split("-").map(Number);
  return new Date(a ?? 1970, (m ?? 1) - 1, g ?? 1).toLocaleDateString("it-IT", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function dataLunga(giorno: string): string {
  const [a, m, g] = giorno.split("-").map(Number);
  const testo = new Date(a ?? 1970, (m ?? 1) - 1, g ?? 1).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

// ------------------------------------------------------------------ pagina

export function RegistroOre() {
  const { utente } = useSessione();
  const [parametri, setParametri] = useSearchParams();
  const predefinito = periodi()[1]!;

  const filtro: FiltroRegistro = {
    dal: parametri.get("dal") ?? predefinito.dal,
    al: parametri.get("al") ?? predefinito.al,
    cliente_id: parametri.get("cliente") ?? undefined,
    pratica_id: parametri.get("pratica") ?? undefined,
    persona: parametri.get("persona") ?? undefined,
    attivita: parametri.get("attivita") ?? undefined,
    testo: parametri.get("testo") ?? undefined,
    stato: (parametri.get("stato") as StatoFatturazione | null) ?? "tutte",
  };
  const gruppo = (parametri.get("per") as Gruppo | null) ?? "cliente";
  const chiave = parametri.toString();

  const registro = useCarica<Registro>(() => ore.registro(filtro), [chiave]);
  const clienti = useCarica<Cliente[]>(() => anagrafica.clienti(), []);
  const pratiche = useCarica<Pratica[]>(() => anagrafica.pratiche(), []);
  const catalogo = useCarica<Attivita[]>(() => anagrafica.catalogo(), []);

  function cambia(nuovi: Record<string, string | undefined>) {
    const prossimi = new URLSearchParams(parametri);
    for (const [k, v] of Object.entries(nuovi)) {
      if (v === undefined || v === "") prossimi.delete(k);
      else prossimi.set(k, v);
    }
    setParametri(prossimi, { replace: true });
  }

  // Le parole si applicano mentre si scrive, ma non a ogni tasto: un attimo
  // di pausa, poi una richiesta sola.
  const [testo, setTesto] = useState(filtro.testo ?? "");
  useEffect(() => {
    if (testo === (parametri.get("testo") ?? "")) return;
    const attesa = setTimeout(() => cambia({ testo: testo.trim() }), 300);
    return () => clearTimeout(attesa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testo]);

  const r = registro.dati;
  const praticheDelCliente = (pratiche.dati ?? []).filter(
    (p) => p.cliente.identificativo === filtro.cliente_id,
  );
  const filtriAttivi = ["cliente", "pratica", "persona", "attivita", "testo", "stato"].some((k) =>
    parametri.has(k),
  );
  const periodoScelto = periodi().find((p) => p.dal === filtro.dal && p.al === filtro.al);

  const [esportando, setEsportando] = useState(false);
  const [erroreEsporta, setErroreEsporta] = useState<string | null>(null);

  /** L'Excel lo prepara il server con gli stessi filtri della pagina: tabella
   * con filtri, totali, riepilogo e, per chi vede i documenti, i prezzi. */
  async function esporta() {
    setErroreEsporta(null);
    setEsportando(true);
    try {
      await ore.esportaExcel(filtro, `ore-${filtro.dal}-${filtro.al}.xlsx`);
    } catch (e) {
      setErroreEsporta(e instanceof ErroreApi ? e.message : "Non sono riuscito a preparare il file.");
    } finally {
      setEsportando(false);
    }
  }

  return (
    <>
      <section className="filtri" aria-label="Filtri">
        <div className="filtri-periodo">
          <div className="segmenti" role="group" aria-label="Periodo">
            {periodi().map((p) => (
              <button
                key={p.nome}
                type="button"
                className={periodoScelto?.nome === p.nome ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                aria-pressed={periodoScelto?.nome === p.nome}
                onClick={() => cambia({ dal: p.dal, al: p.al })}
              >
                {p.nome}
              </button>
            ))}
          </div>
          <div className="filtri-date">
            <label>
              dal
              <input
                type="date"
                value={filtro.dal}
                max={filtro.al}
                onChange={(e) => e.target.value && cambia({ dal: e.target.value })}
              />
            </label>
            <label>
              al
              <input
                type="date"
                value={filtro.al}
                min={filtro.dal}
                onChange={(e) => e.target.value && cambia({ al: e.target.value })}
              />
            </label>
          </div>
        </div>

        <div className="filtri-campi">
          <CampoCerca
            id="filtro-cliente"
            etichetta="Cliente"
            voci={[
              { valore: "", testo: "Tutti i clienti" },
              ...(clienti.dati ?? []).map((c) => ({
                valore: c.identificativo,
                testo: c.denominazione,
                cerca: `${c.codice} ${c.partita_iva}`,
              })),
            ]}
            valore={filtro.cliente_id ?? ""}
            cambia={(v) => cambia({ cliente: v, pratica: undefined })}
            nessuna="Nessun cliente con questo nome"
          />
          {filtro.cliente_id && praticheDelCliente.length > 0 && (
            <div className="campo-scelta">
              <label htmlFor="filtro-pratica">Pratica</label>
              <select
                id="filtro-pratica"
                value={filtro.pratica_id ?? ""}
                onChange={(e) => cambia({ pratica: e.target.value })}
              >
                <option value="">Tutte le pratiche</option>
                {praticheDelCliente.map((p) => (
                  <option key={p.identificativo} value={p.identificativo}>
                    {titoloPratica(p)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {r?.tutte && (
            <div className="campo-scelta">
              <label htmlFor="filtro-persona">Persona</label>
              <select
                id="filtro-persona"
                value={filtro.persona ?? ""}
                onChange={(e) => cambia({ persona: e.target.value })}
              >
                <option value="">Tutte le persone</option>
                {r.persone.map((p) => (
                  <option key={p.identificativo} value={p.identificativo}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <CampoCerca
            id="filtro-attivita"
            etichetta="Prestazione"
            voci={[
              { valore: "", testo: "Tutte le prestazioni" },
              ...(catalogo.dati ?? []).map((a) => ({
                valore: a.codice,
                testo: a.descrizione,
                cerca: a.codice,
              })),
            ]}
            valore={filtro.attivita ?? ""}
            cambia={(v) => cambia({ attivita: v })}
            nessuna="Nessuna prestazione con questo nome"
          />
          <div className="campo-scelta campo-parole">
            <label htmlFor="filtro-testo">Parole</label>
            <input
              id="filtro-testo"
              type="search"
              value={testo}
              placeholder="per esempio nota integrativa"
              onChange={(e) => setTesto(e.target.value)}
            />
          </div>
        </div>

        <div className="filtri-stato">
          <div className="segmenti" role="group" aria-label="Stato della fatturazione">
            {STATI.map((s) => (
              <button
                key={s.valore}
                type="button"
                className={filtro.stato === s.valore ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                aria-pressed={filtro.stato === s.valore}
                onClick={() => cambia({ stato: s.valore === "tutte" ? undefined : s.valore })}
              >
                {s.nome}
              </button>
            ))}
          </div>
          {filtriAttivi && (
            <button
              type="button"
              className="tasto tasto-nudo"
              onClick={() => {
                setTesto("");
                setParametri(new URLSearchParams({ dal: filtro.dal!, al: filtro.al! }), {
                  replace: true,
                });
              }}
            >
              Togli i filtri
            </button>
          )}
        </div>
      </section>

      {registro.errore && (
        <p className="errore" role="alert">
          {registro.errore}
        </p>
      )}

      {r === undefined ? (
        registro.errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <section className="foglio registro">
          <header className="foglio-testa registro-testa">
            <div>
              <h2>{r.righe.length === 0 ? "Nessuna ora" : oreEMinuti(r.minuti)}</h2>
              <p className="narrativa">
                {conta(r.righe.length, "riga", "righe")}
                {r.righe.length > 0 &&
                  (r.minuti_da_fatturare > 0
                    ? `, ${oreEMinuti(r.minuti_da_fatturare)} ancora da fatturare`
                    : ", tutte già fatturate")}
                {!r.tutte && ", solo le tue ore"}
              </p>
            </div>
            <div className="registro-comandi">
              <div className="segmenti" role="group" aria-label="Raggruppa per">
                <span className="scorciatoie-titolo">Raggruppa per</span>
                {GRUPPI.filter((g) => r.tutte || g.valore !== "persona").map((g) => (
                  <button
                    key={g.valore}
                    type="button"
                    className={gruppo === g.valore ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                    aria-pressed={gruppo === g.valore}
                    onClick={() => cambia({ per: g.valore === "cliente" ? undefined : g.valore })}
                  >
                    {g.nome}
                  </button>
                ))}
              </div>
              {r.righe.length > 0 && (
                <button type="button" className="tasto" onClick={esporta} disabled={esportando}>
                  {esportando ? "Preparo il file" : "Esporta in Excel"}
                </button>
              )}
            </div>
          </header>
          {erroreEsporta && (
            <p className="errore" role="alert">
              {erroreEsporta}
            </p>
          )}

          {r.righe.length === 0 ? (
            <p className="foglio-vuoto">
              Nessuna ora con questi filtri. Prova un periodo più largo o togli un filtro.
            </p>
          ) : (
            <Gruppi
              righe={r.righe}
              gruppo={gruppo}
              tutte={r.tutte}
              mie={utente?.identificativo ?? ""}
            />
          )}
        </section>
      )}
    </>
  );
}

function titoloPratica(p: Pratica): string {
  const tipo = nomeTipo(p.tipo);
  return p.descrizione ? `${tipo} ${p.periodo}, ${p.descrizione}` : `${tipo} ${p.periodo}`;
}

// ------------------------------------------------------------------ gruppi

function Gruppi({
  righe,
  gruppo,
  tutte,
  mie,
}: {
  righe: RigaRegistro[];
  gruppo: Gruppo;
  tutte: boolean;
  mie: string;
}) {
  const gruppi = useMemo(() => {
    if (gruppo === "nessuno") return [{ chiave: "", titolo: "", righe }];
    const mappa = new Map<string, { chiave: string; titolo: string; righe: RigaRegistro[] }>();
    for (const i of righe) {
      const chiave =
        gruppo === "cliente" ? (i.cliente_id ?? "") : gruppo === "persona" ? i.persona : i.giorno;
      const titolo =
        gruppo === "cliente" ? i.cliente : gruppo === "persona" ? i.persona_nome : dataLunga(i.giorno);
      if (!mappa.has(chiave)) mappa.set(chiave, { chiave, titolo, righe: [] });
      mappa.get(chiave)!.righe.push(i);
    }
    const elenco = [...mappa.values()];
    // Per giorno dal piu' recente, come arrivano; gli altri per nome.
    return gruppo === "giorno" ? elenco : elenco.sort((a, b) => a.titolo.localeCompare(b.titolo));
  }, [righe, gruppo]);

  // Con molti gruppi e molte righe si parte con i primi due aperti e gli altri chiusi: il totale di ogni
  // gruppo si legge lo stesso, e si apre solo quello che interessa.
  const [apertura, setApertura] = useState<"auto" | "aperti" | "chiusi">("auto");
  const aperto = (indice: number) =>
    apertura === "aperti" ? true : apertura === "chiusi" ? false : righe.length <= 40 || indice < 2;
  const colonnaCliente = gruppo !== "cliente";
  const colonnaPersona = tutte && gruppo !== "persona";
  const colonnaData = gruppo !== "giorno";

  return (
    <>
      {gruppi.length > 2 && gruppo !== "nessuno" && (
        <p className="registro-apri">
          <button type="button" className="tasto tasto-nudo" onClick={() => setApertura("aperti")}>
            Apri tutti
          </button>
          <button type="button" className="tasto tasto-nudo" onClick={() => setApertura("chiusi")}>
            Chiudi tutti
          </button>
        </p>
      )}
      {gruppi.map((g, indice) => (
        <details key={`${g.chiave}-${apertura}`} className="registro-gruppo" open={g.titolo === "" || aperto(indice)}>
          {g.titolo && (
            <summary className="registro-gruppo-testa">
              <h3>{g.titolo}</h3>
              <span className="narrativa">{conta(g.righe.length, "riga", "righe")}</span>
              <span className="cifra">{oreEMinuti(g.righe.reduce((s, i) => s + i.minuti, 0))}</span>
            </summary>
          )}
          <table className="tabella registro-tabella">
            <tbody>
              {g.righe.map((i) => (
                <tr key={i.identificativo}>
                  {colonnaData && <td className="registro-data">{dataCorta(i.giorno)}</td>}
                  {colonnaPersona && <td className="registro-persona">{i.persona_nome}</td>}
                  <td>
                    <span className="registro-lavoro">
                      {colonnaCliente && <strong>{i.cliente}</strong>}
                      {colonnaCliente && ", "}
                      {i.pratica}
                    </span>
                    <span className="narrativa">
                      {i.attivita}
                      {i.quantita > 1 ? ` per ${i.quantita}` : ""}
                      {". "}
                      <span className="registro-descrizione">
                        {i.oggetto}: {i.scopo}
                      </span>
                    </span>
                  </td>
                  <td className="cifra registro-durata">{oreEMinuti(i.minuti)}</td>
                  <td className="registro-stato">
                    {i.fatturata ? (
                      <span className="segno-fatturata">fatturata</span>
                    ) : i.persona === mie ? (
                      <Link className="tasto-nudo" to={`/ore/giornata?giorno=${i.giorno}`}>
                        Apri il giorno
                      </Link>
                    ) : (
                      <span className="segno-da-fatturare">da fatturare</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </>
  );
}
